/**
 * Auth Context
 *
 * Manages authentication state:
 * - Login state
 * - Token refresh logic
 * - Logout function
 * - User information
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '../utils/api';
import { logger } from '../utils/logger';
import { TIMEOUTS } from '../constants/timeouts';

interface User {
  id: string;
  email: string;
  role: string;
  tenantId: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, tenantDomain: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Check if user is authenticated
   */
  const checkAuth = useCallback(async () => {
    const token = apiClient.getAccessToken();
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      if (token) {
        // Token exists, get user from backend
        const userData = await apiClient.getCurrentUser();
        setUser(userData);
        setIsLoading(false);
        return;
      }

      // No token, try to refresh
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error('Refresh token request timeout')),
          TIMEOUTS.REFRESH_TOKEN
        );
      });

      await Promise.race([apiClient.refreshToken(), timeoutPromise]);

      // If refresh succeeds, get user info from backend
      const userData = await apiClient.getCurrentUser();
      setUser(userData);
    } catch (error) {
      setUser(null);
      if (import.meta.env.MODE === 'development') {
        const isExpectedError = error instanceof Error &&
          (error.message.includes('401') ||
           error.message.includes('No refresh token') ||
           error.message.includes('timeout'));
        if (!isExpectedError) {
          logger.info('No refresh token available (first visit or logged out)');
        }
      }
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      setIsLoading(false);
    }
  }, []);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Listen for session expired events (from API interceptor)
   */
  useEffect(() => {
    const handleSessionExpired = () => {
      logger.info('Session expired, logging out');
      setUser(null);
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  /**
   * Login function
   */
  const login = useCallback(async (email: string, password: string, tenantDomain: string) => {
    try {
      setIsLoading(true);
      await apiClient.login(email, password, tenantDomain);

      // Get user info from backend instead of decoding JWT
      const userData = await apiClient.getCurrentUser();
      setUser(userData);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      await apiClient.logout();
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  }, []);

  /**
   * Refresh token function
   */
  const refreshToken = useCallback(async () => {
    try {
      await apiClient.refreshToken();
      // Token refreshed successfully
      // User info should still be valid
    } catch (error) {
      // Refresh failed, clear user
      setUser(null);
      throw error;
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && !!apiClient.getAccessToken(),
    isLoading,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

